import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, CheckCircle, XCircle, AlertTriangle, Pill } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const AdminPharmacies = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.getPharmacyImportStatus();
      setStatus(res.data);
      // Stop polling if import finished
      if (importing && res.data.latest_import?.status !== 'running') {
        setImporting(false);
        if (res.data.latest_import?.status === 'success') {
          toast.success('Importacion de farmacias completada');
        } else if (res.data.latest_import?.status === 'error') {
          toast.error('Error en la importacion de farmacias');
        }
      }
    } catch (error) {
      console.error('Error loading pharmacy status:', error);
    } finally {
      setLoading(false);
    }
  }, [importing]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll while import is running
  useEffect(() => {
    if (!importing) return;
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [importing, loadStatus]);

  const handleImport = async () => {
    setImporting(true);
    try {
      await api.importPharmacies();
      toast.info('Importacion iniciada en segundo plano. Puede tardar varios minutos...');
      // Start polling
      setTimeout(loadStatus, 5000);
    } catch (error) {
      setImporting(false);
      if (error.response?.status === 409) {
        toast.warning('Ya hay una importacion en curso');
      } else {
        toast.error('Error al iniciar la importacion');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100">
        <Loader2 className="animate-spin text-gray-400" size={18} />
        <span className="text-sm text-gray-500">Cargando estado de farmacias...</span>
      </div>
    );
  }

  const latestImport = status?.latest_import;
  const isRunning = latestImport?.status === 'running' || importing;
  const hasError = latestImport?.status === 'error';
  const hasNoData = !status?.has_data;
  const missingZones = latestImport?.details?.missing_zones || [];
  const showWarning = hasError || hasNoData || missingZones.length > 0;

  return (
    <div className="space-y-4" data-testid="admin-pharmacies">
      {/* Status notification */}
      {showWarning && !isRunning && (
        <div
          className={`rounded-2xl p-5 border ${
            hasError || hasNoData
              ? 'bg-red-50 border-red-200'
              : 'bg-amber-50 border-amber-200'
          }`}
          data-testid="pharmacy-warning"
        >
          <div className="flex items-start gap-3">
            {hasError || hasNoData ? (
              <XCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
            ) : (
              <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold text-sm ${hasError || hasNoData ? 'text-red-800' : 'text-amber-800'}`}>
                {hasNoData
                  ? `No hay datos de farmacias para ${status?.current_month}`
                  : hasError
                  ? 'Error en la ultima importacion de farmacias'
                  : `${missingZones.length} zona(s) sin datos`}
              </h3>
              <p className={`text-sm mt-1 ${hasError || hasNoData ? 'text-red-600' : 'text-amber-600'}`}>
                {hasNoData
                  ? 'Es necesario importar los turnos del mes actual para que los usuarios vean las farmacias de guardia.'
                  : hasError
                  ? latestImport?.details?.summary || 'La importacion fallo. Reintenta manualmente.'
                  : `Zonas sin datos: ${missingZones.join(', ')}`}
              </p>
              {latestImport?.finished_at && (
                <p className="text-xs text-gray-400 mt-2">
                  Ultima importacion: {new Date(latestImport.finished_at).toLocaleString('es-ES')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success state */}
      {!showWarning && !isRunning && status?.has_data && (
        <div className="rounded-2xl p-5 border bg-green-50 border-green-200" data-testid="pharmacy-success">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-green-800">
                Farmacias actualizadas ({status.month_entries} turnos para {status.current_month})
              </h3>
              <p className="text-sm text-green-600 mt-1">
                {status.zones?.length || 0} zonas con datos.
                {latestImport?.finished_at && ` Ultima importacion: ${new Date(latestImport.finished_at).toLocaleString('es-ES')}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Running state */}
      {isRunning && (
        <div className="rounded-2xl p-5 border bg-blue-50 border-blue-200" data-testid="pharmacy-importing">
          <div className="flex items-center gap-3">
            <Loader2 className="animate-spin text-blue-500" size={20} />
            <div>
              <h3 className="font-semibold text-sm text-blue-800">Importando farmacias...</h3>
              <p className="text-sm text-blue-600 mt-1">
                Este proceso descarga los PDFs, extrae los turnos y geocodifica las direcciones. Puede tardar 5-10 minutos.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Zones detail */}
      {status?.zones?.length > 0 && !isRunning && (
        <details className="bg-white rounded-2xl border border-gray-100 group">
          <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
            <div className="flex items-center gap-2">
              <Pill className="text-[#006D77]" size={18} />
              <span className="font-semibold text-sm text-[#1D1D1F]">
                Detalle por zona ({status.zones.length} zonas)
              </span>
            </div>
          </summary>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {status.zones.map((z) => (
                <div key={z.zone} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-700">{z.zone}</span>
                  <span className="text-gray-500 font-medium">{z.count} turnos</span>
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      {/* Import button */}
      <Button
        onClick={handleImport}
        disabled={isRunning}
        variant={showWarning ? 'default' : 'outline'}
        className={`rounded-full ${
          showWarning
            ? 'bg-[#006D77] hover:bg-[#005A63] text-white'
            : 'border-[#006D77] text-[#006D77] hover:bg-[#006D77] hover:text-white'
        }`}
        data-testid="pharmacy-import-btn"
      >
        {isRunning ? (
          <Loader2 className="animate-spin mr-2" size={18} />
        ) : (
          <RefreshCw className="mr-2" size={18} />
        )}
        {isRunning ? 'Importando...' : 'Reimportar farmacias'}
      </Button>
    </div>
  );
};

export default AdminPharmacies;
